import * as React from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { MonthChargeTypesValuationModel } from "../../../../../../Wrap/Models/Models/AdviserCharges/MonthChargeTypesValuationModel";
import { AdviceChargesToggleBar } from "./AdviceChargesToggleBar";
import { useState } from "react";
import { JsonDateParser } from "../../../../../../react-core/Utils/JsonDateParser";
import { MoneyDisplayHelper } from "../../../../../../react-core/Helpers/MoneyDisplayHelper";
import { CurrencyCode } from "../../../../../../Wrap/Constants/Enums/Domain/CurrencyCode";
import { Alert } from "../../../../../Shared/Components/Alert";
import { AlertType } from "../../../../../Shared/Entities/Constants/AlertConstants";

interface IChargesColumnChartProps {
    data: MonthChargeTypesValuationModel[];
}

const options = (data: MonthChargeTypesValuationModel[], types: string[]): Highcharts.Options => {
    data = data.slice().reverse();
    return {
        chart: {
            renderTo: "container",
            type: "column",
            backgroundColor: "transparent",
        },
        legend: {
            enabled: false,
        },

        title: {
            text: null,
        },

        credits: {
            enabled: false,
        },

        xAxis: {
            categories: data.map((ele) => {
                return `${JsonDateParser.getMonthNameMapping(
                    ele.MonthOfYear.Month,
                    true
                )} ${ele.MonthOfYear.Year.toString().substr(2, 2)}`;
            }),
            crosshair: true,
        },
        yAxis: {
            min: 0,
            title: {
                text: null,
            },

            labels: {
                formatter: function () {
                    return MoneyDisplayHelper.getMoneyFormat({ Amount: this.value, Currency: CurrencyCode.GBP });
                },
            },
        },
        tooltip: {
            pointFormatter: function () {
                return `${this.series.name}: ${MoneyDisplayHelper.getMoneyFormat({
                    Amount: this.y,
                    Currency: CurrencyCode.GBP,
                })}`;
            },
        },
        plotOptions: {
            column: {
                pointPadding: 0,
                borderWidth: 0,
            },
        },

        series: [
            {
                type: "column",
                name: "Initial adviser charge",
                color: "#0057B7",
                data: data.map((ele) => ele.Valuation.IACTotal.Amount),
            },
            {
                type: "column",
                name: "Ongoing adviser charge",
                color: "#72246c",
                data: data.map((ele) => ele.Valuation.OACTotal.Amount),
            },
            {
                type: "column",
                name: "Regular payment adviser charge",
                color: "#008264",
                data: data.map((ele) => ele.Valuation.RIACTotal.Amount),
            },
            {
                type: "column",
                name: "Adviser ad hoc charge",
                color: "#E24082",
                data: data.map((ele) => ele.Valuation.AACTotal.Amount),
            },
            {
                type: "column",
                name: "Drawdown Initial Adviser Charge",
                color: "#41273B",
                data: data.map((ele) => ele.Valuation.DIACTotal.Amount),
            },
        ].filter((ele) => types.includes(ele.name)) as Highcharts.SeriesOptionsType[],
    };
};

export const ChargesColumnChart = (props: IChargesColumnChartProps) => {
    if (!props.data) { return null; }
    if (props.data.length == 0) {
        return <Alert alertType={AlertType.Warning} message={"No results have been found for the entered criteria."} />;
    }
    const noInitital = props.data.every((charge) => charge?.Valuation?.IACTotal?.Amount == 0);
    const noOngoing = props.data.every((charge) => charge?.Valuation?.OACTotal?.Amount == 0);
    const noRegular = props.data.every((charge) => charge?.Valuation?.RIACTotal?.Amount == 0);
    const noDIAC = props.data.every((charge) => charge?.Valuation?.DIACTotal?.Amount == 0);
    const noAdHoc = props.data.every((charge) => charge?.Valuation?.AACTotal?.Amount == 0);
    const types = [];
    if (!noInitital) { types.push("Initial adviser charge"); }
    if (!noOngoing) { types.push("Ongoing adviser charge"); }
    if (!noRegular) { types.push("Regular payment adviser charge"); }
    if (!noDIAC) { types.push("Drawdown Initial Adviser Charge"); }
    if (!noAdHoc) { types.push("Adviser ad hoc charge"); }
    const [chargeTypes, setChargesTypes] = useState(types.concat());
    if (types.length == 0) { return <Alert alertType={AlertType.Warning} message={"No results have been found for the entered criteria."} />; }

    return (
        <>
            {noInitital && (
                <Alert
                    alertType={AlertType.Warning}
                    message={"No initial adviser charge found for the entered criteria"}
                />
            )}
            {noOngoing && (
                <Alert
                    alertType={AlertType.Warning}
                    message={"No ongoing adviser charge found for the entered criteria"}
                />
            )}
            {noRegular && (
                <Alert
                    alertType={AlertType.Warning}
                    message={"No regular payment adviser charge found for the entered criteria"}
                />
            )}
            {noDIAC && (
                <Alert
                    alertType={AlertType.Warning}
                    message={"No drawdown initial adviser charge found for the entered criteria"}
                />
            )}
            {noAdHoc && (
                <Alert
                    alertType={AlertType.Warning}
                    message={"No adviser ad hoc charge found for the entered criteria"}
                />
            )}
            <HighchartsReact highcharts={Highcharts} options={options(props.data, chargeTypes)} />
            <div className="ml-10 mt-30">
                <AdviceChargesToggleBar
                    onChange={(types) => setChargesTypes(types)}
                    types={chargeTypes}
                    originType={types}
                />
            </div>
        </>
    );
};
